// @ts-nocheck

import { compact, filter, flatten, map, uniq } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BoxV2, ButtonUI, MiddleSummaryBox, useDidUpdate } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { worldTimezones } from 'sonata-static-storage/lib/samples/worldTimezones.samples';

import {
  getInitialTimeFrame,
  getNewTimeFrame,
  getTimeStepForTimeFrame,
  getVariableFilters
} from '@/helpers/reporting.helpers';
import { timeRangeOptionIds } from '@/modules/reporting/ReportingSectionAdvancedFilter/helpers';
import { getCartographyLevels, getVariableValue } from '@/modules/reporting/reporting.resources';
import { getCountriesWithEvents, getSelectedCampaigns, getStaticMode } from '@/store/selectors/reporting.selectors';
import {
  getBeginDate,
  getCampaigns,
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getChartTimeStep,
  getCurrency,
  getEndDate,
  getTablesTimeStep,
  getTimeFrame,
  getTimeRange,
  getTimeZone,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getBaseCurrencyCatalog, getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';
import { ReportingAdvancedFilterModal } from '../ReportingAdvancedFilterModal';
import { ReportingAdvancedFilterResumePanelStyled } from './reportingAdvancedFilterResumePanel.styled';

function ReportingSectionAdvancedFilterResumePanel({ enabled, initialSettings, section = 'general' }) {
  const { formatMessage } = useIntl();
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const baseCurrencyCatalog = useSelector(getBaseCurrencyCatalog);
  const cids = useSelector(getCampaigns);
  const reportingCampaigns = useSelector(getSelectedCampaigns);
  const countries = useSelector(getCountriesWithEvents) || [];
  const defaultCurrency = useSelector(store => getCurrency(store, section));
  const defaultTimeRange = useSelector(store => getTimeRange(store, section));
  const defaultTimeZone = useSelector(store => getTimeZone(store, section));
  const defaultTimeFrame = useSelector(store => getTimeFrame(store, section));
  const defaultChartTimeStep = useSelector(store => getChartTimeStep(store, section));
  const defaultCartographyCountries = useSelector(store => getCartographyCountries(store, section));
  const defaultCartographyLevel = useSelector(store => getCartographyLevel(store, section));
  const defaultCartographyLevelIds = useSelector(store => getCartographyLevelIds(store, section));
  const defaultTablesTimeSteps = useSelector(store => getTablesTimeStep(store, section));
  const defaultBeginDate = useSelector(getBeginDate);
  const defaultEndDate = useSelector(getEndDate);
  const defaultVariableType = useSelector(store => getVariableType(store, section));
  const defaultVariableValues = useSelector(store => getVariableValues(store, section));
  const initialDates = getInitialTimeFrame(reportingCampaigns.filter(({ id }) => cids.includes(id)));

  const [state, setState] = useState({
    cartographyItemOptions: [],
    cartographyLevelOptions: [],
    countriesInfo: undefined,
    currentBeginDate: defaultBeginDate,
    currentCartographyLevel: defaultCartographyLevel,
    currentCartographyLevelIds: defaultCartographyLevelIds,
    currentChartTimeStep: defaultChartTimeStep || initialSettings.timeStep,
    currentCurrency: defaultCurrency || initialSettings.currency,
    currentEndDate: defaultEndDate,
    currentTablesTimeStep: defaultTablesTimeSteps,
    currentTimeFrame: defaultTimeFrame || initialSettings.timeFrame,
    currentTimeRange: defaultTimeRange,
    currentTimeZone: defaultTimeZone,
    currentVariableType: defaultVariableType,
    currentVariableValues: defaultVariableValues,
    currentVariableValuesCopies: [],
    showAdvanceFilterModal: false,
    showDatePicker: defaultTimeRange === 'customDates'
  });

  const {
    cartographyItemOptions,
    cartographyLevelOptions,
    countriesInfo,
    currentCartographyLevel,
    currentCartographyLevelIds,
    currentCurrency,
    currentTimeRange,
    currentTimeZone,
    currentVariableType,
    currentVariableValues,
    currentVariableValuesCopies,
    showAdvanceFilterModal
  } = state;

  const demographyToken = {};
  const cartographyLevelToken = {};
  const variableToken = {};
  const staticMode = useSelector(getStaticMode);

  useEffect(() => {
    return () => {
      demographyToken.cancel && demographyToken.cancel();
      cartographyLevelToken.cancel && cartographyLevelToken.cancel();
      variableToken.cancel && variableToken.cancel();
    };
  }, []);

  useDidUpdate(() => {
    if (currentTimeRange !== 'customDates') {
      const newTimeFrame = getNewTimeFrame(currentTimeRange, initialDates);
      const newTimeStep = getTimeStepForTimeFrame(newTimeFrame);
      const newTablesTimeStep = Object.keys(defaultTablesTimeSteps).reduce((acc, table) => {
        if (table !== 'footfallAttribution' || newTimeStep !== 'hour') {
          return {
            ...acc,
            [table]: newTimeStep
          };
        }
        return {
          ...acc,
          [table]: 'day'
        };
      }, {});
      setState(prevState => ({
        ...prevState,
        currentChartTimeStep: newTimeStep,
        currentTablesTimeStep: newTablesTimeStep,
        currentTimeFrame: newTimeFrame
      }));
    }
  }, [currentTimeRange]);

  const cartographyPermission = checkUserResourcePermissions('reportingFilterCartographyLevels', user, userRoles);
  const variableSelectorsPermission = checkUserResourcePermissions(
    'reportingAdvancedFilterByVariable',
    user,
    userRoles
  );

  const currencyDefaultOption = useMemo(() => {
    if (baseCurrencyCatalog.length === 0) {
      return [
        {
          id: 0,
          label: 'Loading...'
        }
      ];
    }
    return baseCurrencyCatalog
      .map(option => ({
        id: option.baseCurrency,
        label: option.copy
      }))
      .find(cO => cO.id === currentCurrency);
  }, [currentCurrency, baseCurrencyCatalog]);

  const timeRangeDefaultOption = useMemo(() => {
    const optionIds = timeRangeOptionIds;
    return optionIds
      .map(optionId => {
        return {
          id: optionId,
          label: formatMessage({
            defaultMessage: optionId,
            id: `reporting.advancedFilters.date.options.${optionId}`
          })
        };
      })
      .find(entry => entry.id === currentTimeRange);
  }, [currentTimeRange]);

  const timeZoneDefaultOption = useMemo(() => {
    const allTimeZones = flatten(
      map(
        filter(worldTimezones, timezoneGroup => {
          return timezoneGroup.group !== 'UTC' && timezoneGroup.group !== 'Manual Offsets';
        }),
        'zones'
      )
    );
    return allTimeZones
      .map(zone => ({
        id: zone.value,
        label: zone.name
      }))
      .find(option => option.id === currentTimeZone);
  }, [currentTimeZone, worldTimezones]);

  const isDisabledCartographyLevelSelector = useMemo(() => {
    return uniq(countries).length === -1;
  }, [countries]);

  const fetchCartographyLevelSelectorOptions = async () => {
    if (isDisabledCartographyLevelSelector) {
      return;
    }
    try {
      const countriesInfoResponse = await getCartographyDictionary(countries, demographyToken);
      const filteredOptions = countriesInfoResponse[0].tagsConfig.filter(option =>
        uniq(countries).length > 1 ? option.id === 'level0' : option.id !== 'level0' && option.reportingMapEventsEnable
      );
      const newOptions = filteredOptions.map(option => {
        return {
          id: option.id,
          label: option.name
        };
      });
      setState(prevState => ({
        ...prevState,
        cartographyLevelOptions: newOptions,
        countriesInfo: countriesInfoResponse
      }));
    } catch (e) {
      console.info(e.message);
    }
  };

  const defaultCartographyLevelOption = useMemo(() => {
    if (cartographyLevelOptions.length > 0 && currentCartographyLevel) {
      return cartographyLevelOptions.find(cLO => cLO.id === currentCartographyLevel);
    }
    return null;
  }, [cartographyLevelOptions, currentCartographyLevel]);

  const fetchCartographyLevelShapes = async (cartographyLevel, cartographyCountries) => {
    try {
      const responses = await Promise.all(
        cartographyCountries.map(country => getCartographyLevels(cartographyLevel, country, cartographyLevelToken))
      );
      setState(prevState => ({
        ...prevState,
        cartographyItemOptions: flatten(responses)
      }));
    } catch (e) {
      console.info(e.message);
    }
  };

  const defaultCartographyLevelIdsOptions = useMemo(() => {
    return (
      cartographyItemOptions
        .map(option => {
          return {
            id: option.name,
            label: option.description
          };
        })
        .filter(oCLIO => currentCartographyLevelIds.includes(oCLIO.id)) || []
    );
  }, [currentCartographyLevelIds, cartographyItemOptions]);

  const fetchVariableValuesOptions = async variableTypeOption => {
    try {
      const variablesValues = await getVariableValue(
        variableTypeOption,
        cids,
        { timezone: currentTimeZone },
        variableToken
      );
      const filterVariableValues = Object.entries(variablesValues).reduce((acc, [variableKey, variableValue]) => {
        if (currentVariableValues.includes(variableKey)) {
          return {
            ...acc,
            [variableKey]: variableValue
          };
        }
        return acc;
      }, {});
      const variableValuesCopies = Object.values(filterVariableValues).sort();
      setState(prevState => ({
        ...prevState,
        currentVariableValuesCopies: variableValuesCopies
      }));
    } catch (e) {
      console.info(e.message);
    }
  };

  const variableTypeDefaultOption = useMemo(() => {
    return getVariableFilters(user, userRoles, reportingCampaigns)
      .map(variable => ({
        id: variable,
        label: formatMessage({
          defaultMessage: variable,
          id: `reporting.advancedFilters.variables.variableType.options.${variable}`
        })
      }))
      .find(option => option.id === currentVariableType);
  }, [reportingCampaigns, user, userRoles]);

  useEffect(() => {
    fetchCartographyLevelSelectorOptions();
    currentCartographyLevel && fetchCartographyLevelShapes(currentCartographyLevel, countries);
    currentVariableType && fetchVariableValuesOptions(currentVariableType);
  }, []);

  const datesStringValue = useMemo(() => {
    if (timeRangeDefaultOption?.id === 'customDates') {
      return formatMessage(
        {
          defaultMessage: 'Custom dates',
          id: 'reporting.advancedFilters.date.customDatesFormatted'
        },
        {
          beginDate: moment(defaultBeginDate).format('DD-MM-YYYY'),
          endDate: moment(defaultEndDate).format('DD-MM-YYYY')
        }
      );
    }
    return timeRangeDefaultOption?.label || 'N/A';
  }, [timeRangeDefaultOption, defaultBeginDate, defaultEndDate]);

  const countriesStringValue = useMemo(() => {
    if (!countriesInfo) {
      return defaultCartographyCountries?.join(', ');
    }
    if (!defaultCartographyCountries || defaultCartographyCountries.length === 0) {
      return undefined;
    }
    return compact(
      defaultCartographyCountries?.map(countryCode => {
        const foundCountry = countriesInfo?.find(i => i.id.toLowerCase() === countryCode.toLowerCase());
        return foundCountry?.name || undefined;
      })
    ).join(', ');
  }, [defaultCartographyCountries, countriesInfo]);

  const onCloseAdvanceFilterModal = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      showAdvanceFilterModal: false
    }));
  }, []);

  const onClickEditAdvanceFilters = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      showAdvanceFilterModal: true
    }));
  }, []);

  return (
    <ReportingAdvancedFilterResumePanelStyled className="reportingAdvancedFilterResumePanel">
      <BoxV2 id="reportingAdvancedFilterResumePanel-box">
        <BoxV2.Body
          bgColor="background01"
          className="reportingAdvancedFilterResumePanel__boxContainer"
          padding="16px 24px"
        >
          <div className="reportingAdvancedFilterResumePanel__header">
            <div className="alignLeft fontSemiBold reportingAdvancedFilterResumePanel__text">
              {formatMessage({
                defaultMessage: 'Reporting filter view',
                id: 'reporting.advancedFilters.titleFilterView'
              })}
            </div>
            {!staticMode && (
              <div className="reportingAdvancedFilterResumePanel__buttonGroup">
                <ButtonUI
                  className="reportingAdvancedFilterResumePanel__button"
                  icon="far fa-edit"
                  iconPosition="left"
                  onClick={onClickEditAdvanceFilters}
                  shape="link"
                  text={formatMessage({
                    defaultMessage: 'Edit filter',
                    id: 'reporting.advancedFilters.editFilterButton'
                  })}
                  type="secondary"
                />
              </div>
            )}
          </div>
          <div className="reportingAdvancedFilterResumePanel__firstRow">
            <MiddleSummaryBox
              className="reportingAdvancedFilterResumePanel__box"
              contentDirection="column"
              stringValue={currencyDefaultOption?.label || 'N/A'}
              text={formatMessage({
                defaultMessage: 'Currency type',
                id: 'reporting.advancedFilters.currency.title'
              })}
            />
            <MiddleSummaryBox
              className="reportingAdvancedFilterResumePanel__box"
              contentDirection="column"
              stringValue={datesStringValue}
              text={formatMessage({
                defaultMessage: 'Date',
                id: 'reporting.advancedFilters.date.title'
              })}
            />
            <MiddleSummaryBox
              className="reportingAdvancedFilterResumePanel__box"
              contentDirection="column"
              stringValue={timeZoneDefaultOption?.label || 'N/A'}
              text={formatMessage({
                defaultMessage: 'Timezone',
                id: 'reporting.advancedFilters.timezone.title'
              })}
            />
            {!!currentVariableValuesCopies?.length && variableSelectorsPermission && (
              <MiddleSummaryBox
                className="reportingAdvancedFilterResumePanel__box"
                contentDirection="column"
                stringValue={currentVariableValuesCopies.join(', ')}
                text={variableTypeDefaultOption?.label || 'Variable'}
              />
            )}
          </div>
          {cartographyPermission && (
            <div className="reportingAdvancedFilterResumePanel__secondRow">
              {defaultCartographyCountries.length > 0 && (
                <MiddleSummaryBox
                  className="reportingAdvancedFilterResumePanel__box reportingAdvancedFilterResumePanel__cartographyCountries"
                  contentDirection="column"
                  stringValue={countriesStringValue || 'N/A'}
                  text={formatMessage({
                    defaultMessage: 'Countries',
                    id: 'reporting.advancedFilters.cartography.countries.title'
                  })}
                />
              )}
              {defaultCartographyLevelIdsOptions?.length > 0 && (
                <>
                  <MiddleSummaryBox
                    className="reportingAdvancedFilterResumePanel__box reportingAdvancedFilterResumePanel__cartographyLevel"
                    contentDirection="column"
                    stringValue={defaultCartographyLevelOption?.label || 'N/A'}
                    text={formatMessage({
                      defaultMessage: 'Cartography level',
                      id: 'reporting.advancedFilters.cartography.level.title'
                    })}
                  />
                  <MiddleSummaryBox
                    className="reportingAdvancedFilterResumePanel__box reportingAdvancedFilterResumePanel__cartographyFilter"
                    contentDirection="column"
                    stringValue={defaultCartographyLevelIdsOptions.map(i => i.label).join(', ')}
                    text={formatMessage({
                      defaultMessage: 'Cartography filter',
                      id: 'reporting.advancedFilters.cartography.filter.title'
                    })}
                  />
                </>
              )}
            </div>
          )}
          <ReportingAdvancedFilterModal
            enabled={enabled}
            general
            initialSettings={initialSettings}
            isModalOpened={showAdvanceFilterModal}
            onClose={onCloseAdvanceFilterModal}
            section={section}
          />
        </BoxV2.Body>
      </BoxV2>
    </ReportingAdvancedFilterResumePanelStyled>
  );
}

ReportingSectionAdvancedFilterResumePanel.propTypes = {
  enabled: PropTypes.bool, // delete when Reporting is pure React
  initialSettings: PropTypes.shape({
    chartTimeStep: PropTypes.oneOf(['hour', 'day', 'week', 'month', 'year']).isRequired,
    currency: PropTypes.string,
    timeFrame: PropTypes.shape({
      beginDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    timeStep: PropTypes.oneOf(['hour', 'day', 'week', 'month', 'year']).isRequired,
    timeZone: PropTypes.string
  }),
  section: PropTypes.oneOf(['campaignsProgress', 'general', 'insights', 'map', 'summary', 'table'])
};

export default React.memo(ReportingSectionAdvancedFilterResumePanel);
